import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { SparklesIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { getAIConversationById } from "../api/templates";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import ChatMessageContent from "../components/common/ChatMessageContent";

const AIConversationDetail = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { setHeaderContent } = usePageHeader();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!conversationId) return;
    setLoading(true);
    setError(null);
    getAIConversationById(conversationId)
      .then((res) => {
        if (res?.success && res?.data) {
          setConversation(res.data);
        } else {
          setError("Conversation not found");
        }
      })
      .catch((err) => {
        setError(err?.message || "Failed to load conversation");
        toast.error(err?.message || "Failed to load conversation");
      })
      .finally(() => setLoading(false));
  }, [conversationId]);

  const messages = conversation?.messages || [];

  // Set header in Navbar - must run unconditionally (hooks before any returns)
  useEffect(() => {
    if (error) {
      setHeaderContent(
        <button
          type="button"
          onClick={() => navigate("/dashboard/templates/create-ai")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Back to Create with AI
        </button>
      );
    } else if (conversation) {
      setHeaderContent(
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
          <div className="flex flex-col gap-2 min-w-0">
            <BreadCrumb
              items={[
                { label: "Dashboard", href: "/dashboard" },
                { label: "Templates", href: "/dashboard/templates" },
                { label: "Create with AI", href: "/dashboard/templates/create-ai" },
                { label: conversation.title || "Conversation" },
              ]}
            />
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate("/dashboard/templates/create-ai")}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 -ml-1"
              >
                Back
              </button>
            <div className="min-w-0 border-l border-gray-200 pl-3">
              <h1 className="font-heading text-lg sm:text-xl font-bold text-gray-900 truncate">
                {conversation.title || "Conversation"}
              </h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                    conversation.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {conversation.status || "active"}
                </span>
                <span className="text-xs text-gray-500">
                  {messages.length} message{messages.length !== 1 ? "s" : ""}
                </span>
                {conversation.updatedAt && (
                  <span className="text-xs text-gray-400">
                    {new Date(conversation.updatedAt).toLocaleString([], {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
          </div>
          {conversation.documentId && (
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams();
                params.set("documentId", conversation.documentId);
                if (conversation.title) params.set("aiTemplateName", conversation.title);
                navigate(`/dashboard/templates/create?${params.toString()}`);
              }}
              className="flex-shrink-0 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
            >
              Use this document
            </button>
          )}
        </div>
      );
    }
    return () => setHeaderContent(null);
  }, [error, conversation, messages.length, setHeaderContent, navigate]);

  const handleBack = () => {
    navigate("/dashboard/templates/create-ai");
  };

  const handleUseDocument = () => {
    if (!conversation?.documentId) return;
    const params = new URLSearchParams();
    params.set("documentId", conversation.documentId);
    if (conversation.title) params.set("aiTemplateName", conversation.title);
    navigate(`/dashboard/templates/create?${params.toString()}`);
  };

  if (loading) {
    return (
      <PagePermissionGuard module="templates" action="write" pageName="AI Conversation">
        <div className="flex flex-col bg-gray-50 min-h-screen items-center justify-center">
          <div className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
          <p className="text-sm text-gray-500 mt-3">Loading conversation…</p>
        </div>
      </PagePermissionGuard>
    );
  }

  if (error || !conversation) {
    return (
      <PagePermissionGuard module="templates" action="write" pageName="AI Conversation">
        <div className="flex flex-col min-h-[calc(100vh-140px)] items-center justify-center px-4">
          <p className="text-gray-600 mb-4">{error || "Conversation not found"}</p>
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
          >
            Back to Create with AI
          </button>
        </div>
      </PagePermissionGuard>
    );
  }

  return (
    <PagePermissionGuard module="templates" action="write" pageName="AI Conversation">
      <div className="flex flex-col min-h-[calc(100vh-140px)] w-full">
        {/* Chat container - same UI as Create With AI */}
        <main className="flex-1 flex flex-col min-h-0 w-full px-4 py-4">
          <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col min-h-0 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No messages in this conversation.</p>
              ) : (
                <div className="space-y-4 pb-8">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.role === "assistant" && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <SparklesIcon className="h-4 w-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                          msg.role === "user"
                            ? "bg-primary text-white"
                            : "bg-gray-100 border border-gray-200/80 text-gray-900"
                        }`}
                      >
                        <ChatMessageContent
                          content={typeof msg.content === "string" ? msg.content : String(msg.content ?? "")}
                          className="text-sm break-words"
                        />
                      </div>
                      {msg.role === "user" && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserCircleIcon className="h-5 w-5 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {conversation.documentId && (
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-600 mb-3">A document was created from this conversation.</p>
                  <button
                    type="button"
                    onClick={handleUseDocument}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                  >
                    Use this document
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </PagePermissionGuard>
  );
};

export default AIConversationDetail;
